import React, { useState, useEffect } from 'react';
import {
  InputField,
  Dropdown,
  Form,
  Button,
  ConfirmationModal,
  StickySaveBar,
} from 'components';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppDispatch,
  RootState,
  addUserDocument,
  fetchIdCard,
  fetchChildCertificate,
  removeUserDocument,
  createUserDocument,
  findUserDocs,
  deleteUserDocument,
  downloadUserDoc,
} from 'data';
import { editPersonalInfo, fetchPersonalInfo } from 'data/slices/profileSlice';
import { useFormChanges, useScreenSize } from 'hooks';
import { FileIcon } from 'icons';
import {
  ageInYears,
  birthDateInputBounds,
  countries,
  getApiErrorMessage,
  MINIMUM_AGE,
  parseBirthDate,
  workPermitOptions,
} from 'helpers';
import { useTranslation } from 'react-i18next';
import { Document } from 'data/models';
import { Message } from 'primereact/message';
import { Tooltip } from 'primereact/tooltip';

interface PersonalIdentificationFormData {
  nationality: string;
  civilStatus: string;
  numOfChildren: number;
  dateOfBirth: string;
  singleIncome: string;
  religiousDenomination: string;
  crossborderWorker: boolean;
  tarifCode: string;
  frameworkAgreementAccepted: boolean;
  workPermit: string;
  ahvNumber: string;
  salutation: string;
  title: string;
  assignmentAs: string;
}

interface FormSaveHandler {
  save: () => Promise<void>;
  hasChanges: () => boolean;
}

export const PersonalIdentification = ({ user, saveRef }: { user: any; saveRef?: React.MutableRefObject<FormSaveHandler | null> }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch<AppDispatch>();
	const [isIdModalOpen, setIsIdModalOpen] = useState(false);
	const [isBirthCertModalOpen, setIsBirthCertModalOpen] = useState(false);
	const { loading } = useSelector((state: RootState) => state.profile);
	const {
		idCard,
		childCertificate,
		loading: docLoading,
	} = useSelector((state: RootState) => state.document);
	const [childCertsDocs, setChildCertsDocs] = useState<Document[]>([]);
	const [personalDocs, setPersonalDocs] = useState<Document[]>([]);
	const { isSmallScreen } = useScreenSize();
	const [submitted, setSubmitted] = useState(false);
	const [dateOfBirthSaveError, setDateOfBirthSaveError] = useState<string | null>(null);
	const [hasStoredDateOfBirth, setHasStoredDateOfBirth] = useState(false);
	const [frameworkDocs, setFrameworkDocs] = useState<Document[]>([]);

	const [formData, setFormData] = useState<PersonalIdentificationFormData>({
		nationality: '',
		civilStatus: '',
		numOfChildren: 0,
		dateOfBirth: '',
		singleIncome: '',
		religiousDenomination: '',
		crossborderWorker: false,
		tarifCode: '',
		frameworkAgreementAccepted: false,
		workPermit: '',
		ahvNumber: '',
		salutation: '',
		title: '',
		assignmentAs: ''
	});

	const [initialFormData, setInitialFormData] =
		useState<PersonalIdentificationFormData>({
			nationality: '',
			civilStatus: '',
			numOfChildren: 0,
			dateOfBirth: new Date().toISOString().split('T')[0],
			singleIncome: '',
			religiousDenomination: '',
			crossborderWorker: false,
			tarifCode: '',
			frameworkAgreementAccepted: false,
			workPermit: '',
			ahvNumber: '',
			salutation: '',
			title: '',
			assignmentAs: ''
		});

	useEffect(() => {
		dispatch(fetchIdCard());
		dispatch(fetchChildCertificate());
	}, [dispatch]);

	useEffect(() => {
		if (user) {
			const newData = {
				nationality: user.personalInfo.nationality || '',
				civilStatus: user.personalInfo.civilStatus || '',
				numOfChildren: user.personalInfo.numberOfChildren || 0,
				dateOfBirth: user.personalInfo.dateOfBirth
				? user.personalInfo.dateOfBirth.split('T')[0]
				: new Date().toISOString().split('T')[0],
				singleIncome: user.personalInfo.singleIncome || '',
				religiousDenomination: user.personalInfo.religiousDenomination || '',
				crossborderWorker: user.personalInfo.crossBorderWorker || false,
				tarifCode: user.personalInfo.tariffCode || '',
				frameworkAgreementAccepted:
				user.personalInfo.frameworkAgreementAccepted || false,
				workPermit: user.personalInfo.workPermit || '',
				ahvNumber: user.personalInfo.ahvNumber || '',
				salutation: user.personalInfo.salutation || '',
				title: user.personalInfo.title || '',
				assignmentAs: user.personalInfo.assignmentAs || ''
			};
			newData.dateOfBirth = newData.dateOfBirth === '0001-01-01' ? new Date().toISOString().split('T')[0] : newData.dateOfBirth;
			const storedDateOfBirth = user.personalInfo.dateOfBirth
				? user.personalInfo.dateOfBirth.split('T')[0]
				: '';
			setHasStoredDateOfBirth(
				storedDateOfBirth !== '' && storedDateOfBirth !== '0001-01-01',
			);
			setFormData(newData);
			setInitialFormData(newData);

			const childDocs = findUserDocs('child_certificate');
			childDocs?.then((res) => {
				setChildCertsDocs(res);
			}).catch((err) => {
				console.log('err', err)
			});

			const personalDocs = findUserDocs('personal');
			personalDocs?.then((res) => {
				setPersonalDocs(res);
			}).catch((err) => {
				console.log('err', err)
			});

			const frameworkDocs = findUserDocs('framework');
			frameworkDocs?.then((res) => {
				setFrameworkDocs(res);
			}).catch((err) => {
				console.log('err', err)
			});
		}
	}, [user]);

  	const isFormChanged = useFormChanges(initialFormData, formData);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value, type } = e.target;
		if (name === 'dateOfBirth') {
			setDateOfBirthSaveError(null);
		}
		setFormData((prev) => ({
		...prev,
		[name]: type === 'number' ? parseInt(value || '0', 10) : value,
		}));
	};

	const handlePersonalFilesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const { files } = e.target;
		const formData = new FormData();
		if (files && files.length > 0) {
			Array.from(files).forEach((file) => {
				formData.append('files', file);
			});
		}
		try {
			await createUserDocument(formData, 'personal');
			const docs = findUserDocs('personal');
			docs?.then((res) => {
				setPersonalDocs(res);
			}).catch((err) => {
				console.log('err', err)
			});
		} catch (error) {
			console.error('Upload error:', error);
		}
	};

	const handleRemoveIdFile = async () => {
		if (idCard?.id) {
		try {
			await dispatch(removeUserDocument({ id: idCard.id })).unwrap();
			dispatch(fetchIdCard());
		} catch (error) {
			console.error('Error removing ID document:', error);
		}
		}
		setIsIdModalOpen(false);
	};

	const handleBirthCertificateUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const { files } = e.target;
		const formData = new FormData();
		if (files && files.length > 0) {
			Array.from(files).forEach((file) => {
				formData.append('files', file);
			});
		}
		try {
			await createUserDocument(formData, 'child_certificate');
			const childDocs = findUserDocs('child_certificate');
			childDocs?.then((res) => {
				setChildCertsDocs(res);
			}).catch((err) => {
				console.log('err', err)
			});
		} catch (error) {
			console.error('Upload error:', error);
		}
	};

	const handleRemoveBirthCertificate = async () => {
		if (childCertificate?.id) {
		try {
			await dispatch(
			removeUserDocument({ id: childCertificate.id }),
			).unwrap();
			dispatch(fetchChildCertificate());
		} catch (error) {
			console.error('Error removing birth certificate:', error);
		}
		}
		setIsBirthCertModalOpen(false);
	};

	const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
			const { name, value } = e.target;
			setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const formattedDateOfBirth =
		formData.dateOfBirth && formData.dateOfBirth !== ''
		? `${formData.dateOfBirth}T00:00:00Z`
		: '1970-01-01T00:00:00Z';

	const birthDate = parseBirthDate(formData.dateOfBirth);
	const dateOfBirthIssue = !birthDate
		? 'invalid'
		: ageInYears(birthDate) < MINIMUM_AGE
		? 'underMinimumAge'
		: null;

	// A user who never set a date of birth gets today's date pre-filled, which is
	// itself under age — don't accuse them of that until they touch the field or
	// save. A date that really is stored gets flagged straight away.
	const showDateOfBirthIssue =
		!!dateOfBirthIssue &&
		(hasStoredDateOfBirth ||
			submitted ||
			formData.dateOfBirth !== initialFormData.dateOfBirth);

	const dateOfBirthError =
		dateOfBirthSaveError ??
		(!showDateOfBirthIssue
			? undefined
			: dateOfBirthIssue === 'underMinimumAge'
			? (t('personalIdentification.dateOfBirthUnderMinimumAge') as string)
			: (t('personalIdentification.dateOfBirthInvalid') as string));

	const birthDateBounds = birthDateInputBounds();

	const handleSavePersonalInfo = async () => {
		setSubmitted(true);
		setDateOfBirthSaveError(null);
		if (!formData.nationality || !formData.civilStatus || !formData.ahvNumber || !formData.salutation) {
			return;
		}
		if (dateOfBirthIssue) {
			return;
		}
		try {
			const personalInfoPayload = {
				ahvNumber: formData.ahvNumber,
				nationality: formData.nationality,
				civilStatus: formData.civilStatus,
				numberOfChildren: formData.numOfChildren,
				dateOfBirth: formattedDateOfBirth,
				singleIncome: formData.singleIncome,
				religiousDenomination: formData.religiousDenomination,
				crossBorderWorker: formData.crossborderWorker,
				tariffCode: formData.tarifCode,
				frameworkAgreementAccepted: formData.frameworkAgreementAccepted,
				workPermit: formData.workPermit,
				identityCard: idCard?.url || null,
				birthCertificate: childCertificate?.url || null,
				salutation: formData.salutation,
				title: formData.title,
				assignmentAs: formData.assignmentAs
			};

			await dispatch(editPersonalInfo(personalInfoPayload)).unwrap();
				console.log(
					t('personalIdentification.toast.personalInfoUpdatedSuccessfully'),
				);
			setInitialFormData({ ...formData });
			dispatch(fetchPersonalInfo()).unwrap();
		} catch (err) {
			// editPersonalInfo rejects with the response body itself, not an axios error.
			const message = getApiErrorMessage({ response: { data: err } });
			if (/at least 18 years old|under 18/i.test(message)) {
				setDateOfBirthSaveError(
					t('personalIdentification.dateOfBirthUnderMinimumAge') as string,
				);
			}
			console.error(
				t('personalIdentification.toast.personalInfoUpdateFailed'),
				err,
			);
		}
	};

	// Expose save handler and form change status via ref
	useEffect(() => {
		if (saveRef) {
			saveRef.current = {
				save: handleSavePersonalInfo,
				hasChanges: () => isFormChanged,
			};
		}
	}, [saveRef, isFormChanged, handleSavePersonalInfo]);

	const isSwitzerland = formData.nationality === 'CH';
	const hasNationality = formData.nationality !== '';
	const isCPermit = formData.workPermit === 'Permit C EU/EFTA';

	const nationalityOptions = [
		{ label: t('personalIdentification.select'), value: '' },
		...countries.map((country) => ({
		label: t(country.labelKey),
		value: country.value,
		})),
	];

	const translatedWorkPermitOptions = workPermitOptions.map((option) => ({
		label: t(option.labelKey),
		value: option.value,
	}));

	const deleteChildCertDoc = (docId: number) => async () => {
		try {
			const resp = await deleteUserDocument(docId);
			if (resp) {
				setChildCertsDocs((prevDocs) => prevDocs.filter((doc) => doc.id !== docId));
			}
		} catch (error) {
			console.error('Failed to delete document:', error);
		}
	}

	const deletePersonalDoc = (docId: number) => async () => {
		try {
			const resp = await deleteUserDocument(docId);
			if (resp) {
				setPersonalDocs((prevDocs) => prevDocs.filter((doc) => doc.id !== docId));
			}
		} catch (error) {
			console.error('Failed to delete document:', error);
		}
	}

	const downloadFrameworkDoc = async (e: any, id: number, docType: string) => {
		e.preventDefault();
	
		try {
			const response = await downloadUserDoc(id, docType);
	
			const { data } = response;
			let filename = data.filename
	
			// Assume raw base64 without data URI prefix
			const base64 = typeof data.data === 'string' ? data.data.trim() : '';
	
			// Use fetch with a data URL for safer decoding
			const blob = await fetch(`data:application/octet-stream;base64,${base64}`).then(res => res.blob());
	
			const link = document.createElement('a');
			link.href = URL.createObjectURL(blob);
			link.download = filename || 'invoice_document';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (error) {
			console.log('downloadInvoiceDoc.error', error);
		}
	};

	const agreementAccepted = Boolean(
		user.personalInfo?.frameworkAgreementAccepted,
	);
	const agreementAccent = agreementAccepted
		? {
			border: 'border-[#50b154]',
			background: 'bg-[#f0fdf4]',
			text: 'text-[#50b154]',
			pill: 'bg-[#50b154]',
			icon: 'pi-check-circle',
		}
		: {
			border: 'border-[#FF6F61]',
			background: 'bg-[#FF6F611A]',
			text: 'text-[#FF6F61]',
			pill: 'bg-[#FF6F61]',
			icon: 'pi-info-circle',
		};

	return (
		<>
		<Form
			content={
			<form className="flex flex-col gap-4">
				<div className="text-[20px] font-medium">
				{t('personalIdentification.title')}
				</div>

				<Dropdown
					label={t('personalIdentification.nationality')}
					name="nationality"
					value={formData.nationality || ''}
					options={nationalityOptions}
					onChange={handleDropdownChange}
					error={!formData.nationality ? t('general.required') as string : undefined}
				/>
				{hasNationality && !isSwitzerland && (
				<Dropdown
					label={t('personalIdentification.workPermit')}
					name="workPermit"
					value={formData.workPermit || ''}
					options={translatedWorkPermitOptions}
					onChange={handleDropdownChange}
					error={!formData.workPermit ? t('general.required') as string : undefined}
				/>
				)}

				<div className="flex flex-col gap-2">
					<label className="font-medium">
						{t('personalIdentification.idPassportPermit')}
					</label>
					<InputField
						label={t('personalIdentification.uploadIdentification')}
						name="copyOfIdentification"
						type="file"
						multiple
						value=""
						onChange={handlePersonalFilesChange}
						placeholder={t(
							'personalIdentification.uploadIdentificationPlaceholder',
						)}
						inputHeight={100}
					/>
					{personalDocs.length > 0 && (
						personalDocs.map((doc, index) => (
							<div>
								<div className="flex items-center gap-4">
									<button
										type="button"
										key={index}
										className="flex items-center gap-2 underline text-[#4F5057] whitespace-nowrap">
										<FileIcon />
										{isSmallScreen && (
											<span>
												{`${doc.filename.slice(0, 9)}...`}
											</span>
										)}
										{!isSmallScreen && (
											<span>
												{doc.filename.length > 30 ? `${doc.filename.slice(0, 30)}...` : doc.filename}
											</span>
										)}
									</button>
									<a href="" onClick={() => window.open(doc.url, '_blank')}>
										<span className={`inline-block px-3 py-1 ml-2 rounded-full text-white font-medium text-sm text-center bg-[#50b154]`}>
											<i className="pi pi-download"></i>
										</span>
									</a>
									<a className={`mt-1`} href="" onClick={deletePersonalDoc(doc.id)}>
										<i style={{ fontSize: '1.3rem' }} className="pi pi-trash"></i>
									</a>
										<div>
											{doc.idVerified == undefined && doc.idVerified == null && (
												<span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFF4E5] text-[#B45309] text-sm font-medium whitespace-nowrap">
													<i className="pi pi-clock"></i>
													{t('personalIdentification.pending')}
												</span>
											)}
											{doc.idVerified == false && (
												<div>
													<Tooltip target=".id-declined" />
													<img
														data-pr-tooltip={t('personalIdentification.declined') || ''}
														src={require('../../images/id-verification-decline.png')} 
														className="w-12 id-declined"
													/>
												</div>
											)}
											{doc.idVerified == true && doc.idVerified != undefined && doc.idVerified != null && (
												<span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E8F5E9] text-[#2E7D32] text-sm font-medium whitespace-nowrap">
													<i className="pi pi-check"></i>
													{t('personalIdentification.verified')}
												</span>
											)}
										</div>
								</div>
								{doc.idVerified == undefined && doc.idVerified == null && (
									<p className="mt-3 mb-2 text-sm text-[#B45309]">
										{t('personalIdentification.pendingReview')}
									</p>
								)}
							</div>
						))
					)}
				</div>

				<Dropdown
					label={t('personalIdentification.salutation')}
					name="salutation"
					value={formData.salutation || ''}
					options={[
						{ label: t('personalIdentification.select'), value: '' },
						{
							label: t('personalIdentification.mr'),
							value: 'mr',
						},
						{
							label: t('personalIdentification.mrs'),
							value: 'mrs',
						}
					]}
					onChange={handleDropdownChange}
					error={!formData.salutation ? t('general.required') as string : undefined}
				/>

				<InputField
					label={t('personalIdentification.userTitle')}
					name="title"
					type="text"
					value={formData.title}
					onChange={handleChange}
					placeholder='Title'
				/>

				<InputField
					label={t('personalIdentification.assignmentAs')}
					name="assignmentAs"
					type="text"
					value={formData.assignmentAs}
					onChange={handleChange}
					placeholder={t('personalIdentification.assignmentAs')}
				/>

				<InputField
					label={t('personalIdentification.ahvNumber')}
					name="ahvNumber"
					type="text"
					value={formData.ahvNumber}
					onChange={handleChange}
					placeholder='AHV Number'
					error={!formData.ahvNumber ? t('general.required') as string : undefined}
				/>

				<Dropdown
					label={t('personalIdentification.civilStatus')}
					name="civilStatus"
					value={formData.civilStatus || ''}
					options={[
						{ label: t('personalIdentification.select'), value: '' },
						{ label: t('personalIdentification.single'), value: 'Single' },
						{
						label: t('personalIdentification.married'),
						value: 'Married',
						},
						{
						label: t('personalIdentification.divorced'),
						value: 'Divorced',
						},
					]}
					onChange={handleDropdownChange}
					error={!formData.civilStatus ? t('general.required') as string : undefined}
				/>

				<InputField
					label={t('personalIdentification.numberOfChildren')}
					name="numOfChildren"
					type="number"
					value={formData.numOfChildren.toString()}
					onChange={(e) => {
						const value = parseInt(e.target.value, 10);
						setFormData((prev) => ({
							...prev,
							numOfChildren: value >= 0 ? value : 0,
						}));
					}}
					placeholder={t('personalIdentification.numberOfChildrenPlaceholder')}
				/>

				{formData.numOfChildren !== 0 && (
				<div className="flex flex-col gap-2">
					<InputField
						label={t('personalIdentification.uploadBirthCertificate')}
						name="birthCertificate"
						type="file"
						value=""
						onChange={handleBirthCertificateUpload}
						placeholder={t(
						'personalIdentification.uploadBirthCertificatePlaceholder',
						)}
						inputHeight={100}
						multiple
					/>
					{childCertsDocs.length > 0 && (
						childCertsDocs.map((doc, index) => (
							<div className="flex items-center gap-4">
								<button
									type="button"
									key={index}
									className="flex items-center gap-2 underline text-[#4F5057] whitespace-nowrap">
									<FileIcon />
									{isSmallScreen && (
										<span>
											{`${doc.filename.slice(0, 9)}...`}
										</span>
									)}
									{!isSmallScreen && (
										<span>
											{doc.filename.length > 30 ? `${doc.filename.slice(0, 30)}...` : doc.filename}
										</span>
									)}
								</button>
								<a href="" onClick={() => window.open(doc.url, '_blank')}>
									<span className={`inline-block px-3 py-1 ml-2 rounded-full text-white font-medium text-sm text-center bg-[#50b154]`}>
										<i className="pi pi-download"></i>
									</span>
								</a>
								<a className={`mt-1`} href="" onClick={deleteChildCertDoc(doc.id)}>
									<i style={{ fontSize: '1.3rem' }} className="pi pi-trash"></i>
								</a>
							</div>
						))
					)}
				</div>
				)}

				<InputField
				label={t('personalIdentification.dateOfBirth')}
				name="dateOfBirth"
				type="date"
				value={formData.dateOfBirth || ''}
				onChange={handleChange}
				min={birthDateBounds.min}
				max={birthDateBounds.max}
				error={dateOfBirthError}
				/>

				{formData.nationality != '' && !isSwitzerland && formData.workPermit != '' && !isCPermit && (
				<>
					{/* <InputField
					label={t('personalIdentification.singleIncome')}
					name="singleIncome"
					type="number"
					value={formData.singleIncome.toString()}
					onChange={handleChange}
					placeholder={t(
						'personalIdentification.singleIncomePlaceholder',
					)}
					/> */}

					<Dropdown
						label={t('personalIdentification.singleIncome')}
						name="singleIncome"
						value={formData.singleIncome || ''}
						options={[
							{ label: t('personalIdentification.select'), value: '' },
							{
								label: t('personalIdentification.yes'),
								value: 'yes',
							},
							{
								label: t('personalIdentification.no'),
								value: 'no',
							}
						]}
						onChange={handleDropdownChange}
						error={!formData.singleIncome ? t('general.required') as string : undefined}
					/>

					<Dropdown
						label={t('personalIdentification.religiousDenomination')}
						name="religiousDenomination"
						value={formData.religiousDenomination || ''}
						options={[
							{ label: t('personalIdentification.select'), value: '' },
							{
							label: t('personalIdentification.christian'),
							value: 'Christian',
							},
							{
							label: t('personalIdentification.muslim'),
							value: 'Muslim',
							},
							{
							label: t('personalIdentification.jewish'),
							value: 'Jewish',
							},
							{
							label: t('personalIdentification.other'),
							value: 'Other',
							},
						]}
						onChange={handleDropdownChange}
						error={!formData.religiousDenomination ? t('general.required') as string : undefined}
					/>

					<label className="flex items-center gap-2 text-[#4F5057]">
					<input
						type="checkbox"
						name="crossborderWorker"
						checked={formData.crossborderWorker}
						onChange={(e) =>
						setFormData((prev) => ({
							...prev,
							crossborderWorker: e.target.checked,
						}))
						}
						className="checked:accent-[#f8584a] h-5 w-5"
					/>
					{t('personalIdentification.crossborderWorker')}
					</label>

					<InputField
					label={t('personalIdentification.tarifCode')}
					name="tarifCode"
					value={formData.tarifCode || ''}
					onChange={handleChange}
					placeholder={t('personalIdentification.tarifCodePlaceholder')}
					disable
					/>
				</>
				)}

				{frameworkDocs.length > 0 ? (
					<div className={`border-2 ${agreementAccent.border} ${agreementAccent.background} rounded-lg p-4 mt-4`}>
						<div className="flex items-start gap-3 mb-4">
							<i className={`pi ${agreementAccent.icon} ${agreementAccent.text} text-2xl mt-1`}></i>
							<div className="flex-1">
								<h3 className={`text-[18px] font-semibold ${agreementAccent.text} mb-2`}>
									{agreementAccepted
										? t('personalIdentification.frameworkAgreementConfirmed')
										: t('personalIdentification.frameworkAgreementRequired')}
								</h3>
								<p className="text-[14px] text-[#4F5057] mb-4">
									{agreementAccepted
										? t('personalIdentification.frameworkAgreementConfirmedMessage')
										: t('personalIdentification.frameworkAgreementMessage')}
								</p>
							</div>
						</div>
						{frameworkDocs.map((doc, index) => (
							<div key={index} className="flex items-center gap-4 mb-4 bg-white p-3 rounded border border-[#D5D9DF]">
								<button
									type="button"
									className="flex items-center gap-2 underline text-[#4F5057] whitespace-nowrap">
									<FileIcon />
									{isSmallScreen && (
										<span>
											{`${doc.filename.slice(0, 9)}...`}
										</span>
									)}
									{!isSmallScreen && (
										<span>
											{doc.filename.length > 30 ? `${doc.filename.slice(0, 30)}...` : doc.filename}
										</span>
									)}
								</button>
								<a href="" onClick={(e) => downloadFrameworkDoc(e, doc.id, 'framework')}>
									<span className={`inline-block px-3 py-1 ml-2 rounded-full text-white font-medium text-sm text-center ${agreementAccent.pill}`}>
										<i className="pi pi-download"></i>
									</span>
								</a>
							</div>
						))}
						<div className="bg-white p-4 rounded border border-[#D5D9DF]">
							<label className="flex items-center gap-3 text-[#4F5057] cursor-pointer">
								<input
									type="checkbox"
									disabled={user.personalInfo.frameworkAgreementAccepted}
									name="frameworkAgreementAccepted"
									checked={formData.frameworkAgreementAccepted}
									onChange={(e) =>
									setFormData((prev) => ({
										...prev,
										frameworkAgreementAccepted: e.target.checked,
									}))
									}
									className="checked:accent-[#50b154] h-6 w-6"
								/>
								<span className="text-[16px] font-medium">
									{t('personalIdentification.acceptFrameworkAgreement')}
								</span>
							</label>
						</div>
					</div>
				) : (
					<div className="border-2 border-[#FF6F61] bg-[#FF6F611A] rounded-lg p-4 mt-4">
						<div className="flex items-start gap-3 mb-4">
							<i className="pi pi-info-circle text-[#FF6F61] text-2xl mt-1"></i>
							<div className="flex-1">
								<h3 className="text-[18px] font-semibold text-[#FF6F61] mb-2">
									{t('personalIdentification.frameworkAgreementPending') || 'Framework Contract Pending'}
								</h3>
								<p className="text-[14px] text-[#4F5057] mb-4">
									{t('personalIdentification.frameworkAgreementPendingMessage') || 'Your framework contract is being prepared. You will be able to review and accept it once it\'s ready.'}
								</p>
							</div>
						</div>
						<div className="bg-white p-4 rounded border border-[#D5D9DF]">
							<label className="flex items-center gap-3 text-[#4F5057] cursor-not-allowed opacity-60">
								<input
									type="checkbox"
									disabled={true}
									name="frameworkAgreementAccepted"
									checked={false}
									className="h-6 w-6"
								/>
								<span className="text-[16px] font-medium">
									{t('personalIdentification.acceptFrameworkAgreement')}
								</span>
							</label>
						</div>
					</div>
				)}
			</form>
			}
			maxWidth={608}
		/>

		<StickySaveBar>
			<Button
			onClick={handleSavePersonalInfo}
			label={t('personalIdentification.saveChanges')}
			loading={loading || docLoading}
			disabled={loading || docLoading || !isFormChanged}
			style={{
				opacity: loading || docLoading || !isFormChanged ? 0.5 : 1,
			}}
			/>
		</StickySaveBar>

		<ConfirmationModal
			isOpen={isIdModalOpen}
			onClose={() => setIsIdModalOpen(false)}
			onConfirm={handleRemoveIdFile}
			loading={loading || docLoading}
			title={t('personalIdentification.confirmRemoveId')}
			message={t('personalIdentification.confirmRemoveIdMessage')}
		/>

		<ConfirmationModal
			isOpen={isBirthCertModalOpen}
			onClose={() => setIsBirthCertModalOpen(false)}
			onConfirm={handleRemoveBirthCertificate}
			loading={loading || docLoading}
			message="Do you want to remove the birth certificate?"
		/>
		</>
	);
};
